import React, { useState, useEffect } from 'react';
import { getColaboradores, createColaborador, updateColaborador, deleteColaborador, importColaboradores } from '../services/colaboradoresServices';
import ReactPaginate from 'react-paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faHardHat, faUpload, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'bootstrap';
import ColaboradorForm from './ColaboradorForm';
import { showErrorAlert, showSuccessAlert } from '../services/alertService';
import Swal from 'sweetalert2';

const Colaboradores = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCollaborators(name, type, currentPage, itemsPerPage);
  }, [name, type, currentPage]);

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map((tooltipTriggerEl) => new Tooltip(tooltipTriggerEl));
  }, []);

  const fetchCollaborators = async (name, type, page, limit) => {
    setLoading(true);
    try {
      const data = await getColaboradores(name, page, limit);
      setCollaborators(data.colaboradores);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Failed to fetch collaborators', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page on search
    fetchCollaborators(name, type, 0, itemsPerPage);
  };

  const handleShowForm = (collaborator = null) => {
    setSelectedCollaborator(collaborator);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedCollaborator(null);
  };

  const handleSave = async (formData) => {
    try {
      let response = null;
      if (selectedCollaborator) {
        response = await updateColaborador(selectedCollaborator.id, formData);
      } else {
        response = await createColaborador(formData);
      }
      if(response){
        showSuccessAlert("El colaborador se ha registrado correctamente.");
        handleCloseForm();
      }
      fetchCollaborators(name, type, currentPage, itemsPerPage); // Refresh the list
    } catch (error) {
      showErrorAlert("Failed to save collaborator");
      console.error('Failed to save collaborator', error);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
        title: '¿Estás seguro?',
        text: "¡No podrás revertir esto!, las actividades se eliminarán también.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminarlo'
      }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                const response = await deleteColaborador(id);
                showSuccessAlert("El colaborador se ha eliminado correctamente.");
                fetchCollaborators(name, type, currentPage, itemsPerPage); // Refresh the list
              } catch (error) {
                  showErrorAlert("Failed to delete collaborator");
                console.error('Failed to delete collaborator', error);
              }
        }
      });
    
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      Swal.fire('Error', 'Solo se permiten archivos de tipo Excel', 'error');
      return;
    }

    Swal.fire({
      title: 'Procesando archivo...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const result = await importColaboradores(file);
      Swal.fire(
        'Importación completada',
        `Registros exitosos: ${result.registrosExitosos}, Registros fallidos: ${result.registrosFallidos}`,
        'success'
      );
      fetchCollaborators(name, type, currentPage + 1, itemsPerPage); // Refresh the list
    } catch (error) {
      console.error('Failed to import collaborators', error);
      showErrorAlert("hubo un problema al importar el archivo");
    }
  };

  return (
    <div className="collaborators container mt-4">
      <form className="d-flex mb-4" onSubmit={handleSearch}>
        <input
          type="text"
          className="form-control me-2"
          placeholder="Nombre, Apellido o Cédula"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select
          className="form-select me-2"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">Seleccione Tipo</option>
          <option value="auxiliar">Auxiliar</option>
          <option value="fijo">Fijo</option>
        </select>
        <button type="submit" className="btn btn-primary me-2">Consultar</button>
        <label className="btn btn-secondary me-2" htmlFor="importFile">
          <FontAwesomeIcon icon={faUpload} />
          <span className="ms-2">Importar</span>
        </label>
        <input
          type="file"
          id="importFile"
          style={{ display: 'none' }}
          onChange={handleImport}
        />
        <button type="button" className="btn btn-success" onClick={() => handleShowForm()}>
          <FontAwesomeIcon icon={faPlus} />
          <span className="ms-2">Registrar</span>
        </button>
      </form>
      <table className="table table-striped">
        <thead>
          <tr>
            <th scope="col">Cédula</th>
            <th scope="col">Apellido</th>
            <th scope="col">Nombre</th>
            <th scope="col">Naturaleza</th>
            <th scope="col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {collaborators.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">No hay registros que mostrar</td>
            </tr>
          ) : (
            collaborators.map((collaborator) => (
              <tr key={collaborator.id}>
                <td className="text-primary" style={{cursor: 'pointer'}} onClick={() => handleShowForm(collaborator)}>
                  {collaborator.cedula}
                </td>
                <td>{collaborator.apellido}</td>
                <td>{collaborator.nombre}</td>
                <td>
                  {collaborator.esFijo && (
                    <FontAwesomeIcon
                      icon={faHardHat}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Líder"
                    />  
                  )}
                  {collaborator.esAuxiliar && (
                    <FontAwesomeIcon
                      icon={faUser}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Auxiliar"
                    />
                  )}
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(collaborator.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {collaborators.length > 0 && (
        <ReactPaginate
          previousLabel={'Previous'}
          nextLabel={'Next'}
          breakLabel={'...'}
          pageCount={totalPages}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={'pagination justify-content-center'}
          pageClassName={'page-item'}
          pageLinkClassName={'page-link'}
          previousClassName={'page-item'}
          previousLinkClassName={'page-link'}
          nextClassName={'page-item'}
          nextLinkClassName={'page-link'}
          breakClassName={'page-item'}
          breakLinkClassName={'page-link'}
          activeClassName={'active'}
        />
      )}
      <ColaboradorForm
        show={showForm}
        handleClose={handleCloseForm}
        collaborator={selectedCollaborator}
        onSave={handleSave}
      />
    </div>
  );
};

export default Colaboradores;
